import { useState, memo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Card, Col, Row, Button, Image, OverlayTrigger, Tooltip } from "react-bootstrap";
import "../css/cards.css";
import OverlayText from "./OverlayText";
import StatusIndicator from "./StatusIndicator";
import SewMiniMap from "./SewMiniMap";
import { getFlagLink, timeDifference } from "../utils/Utils";
import { FaFolder } from "react-icons/fa";
import PropTypes from "prop-types";

const formatCoord = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? num.toFixed(4) : "N/A";
};

// Altitude is stored in kilometres; display it in metres MSL (e.g. 0.015 -> "15 MSL").
const formatAltitude = (value) => {
    const num = parseFloat(value);
    return Number.isFinite(num) ? `${Math.round(num * 1000)} MSL` : "N/A";
};

const SewCard = ({ asset, imageUrl }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [showImage, setShowImage] = useState(true);
    const [showImagery, setShowImagery] = useState(true);

    const isDossierPage = location.pathname.includes("/dossier/sew/");

    const openInNewTab = () => {
        return window.open(`/dossier/sew/${asset?.Id}`, "_blank", "noopener,noreferrer");
    };

    const handleClick = () => navigate(`/dossier/sew/${asset?.Id}`);

    const renderTooltip = (text) => (
        <Tooltip id="sew-card-tooltip">
            {text}
        </Tooltip>
    );

    return (
        <Card
            style={{
                backgroundColor: "#181b1f",
                color: "white",
                height: !isDossierPage ? "100%" : "",
                borderRadius: "5px"
            }}
        >
            {imageUrl && showImagery && (
                <Image
                    style={{
                        width: "100%",
                        display: "block",
                        borderTopLeftRadius: "5px",
                        borderTopRightRadius: "5px"
                    }}
                    alt={`${asset?.Name || "SEW"} imagery`}
                    src={imageUrl}
                    onError={() => setShowImagery(false)}
                />
            )}

            <SewMiniMap latitude={asset?.Latitude} longitude={asset?.Longitude} status={asset?.Status} />

            {showImage && getFlagLink(asset) && (
                <Image
                    style={{
                        width: "50px",
                        height: "50px",
                        position: "absolute",
                        zIndex: 9999,
                        top: 5,
                        right: 5
                    }}
                    alt={asset?.CountryId}
                    src={getFlagLink(asset)}
                    onError={() => setShowImage(false)}
                />
            )}

            <Card.Body>
                <Card.Title className="card-text bold">
                    ({asset?.Id}) {asset?.Name}
                </Card.Title>
                <div style={{ fontSize: "12px", marginBottom: "10px" }}>
                    {asset?.Classification ? `Classification: ${asset.Classification}` : ""}
                </div>

                <div className="card-text">
                    <Row className="bold">
                        <Col xs={4}>Type</Col>
                        <Col xs={4}>Status</Col>
                        <Col xs={4}>Last Comms</Col>
                    </Row>
                    <Row>
                        <Col xs={4}>
                            <OverlayText text={asset?.Type || "N/A"} maxLength={8} />
                        </Col>
                        <Col xs={4}>
                            <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                                <StatusIndicator status={asset?.Status} />
                                <OverlayText text={asset?.Status || "N/A"} maxLength={6} />
                            </span>
                        </Col>
                        <Col xs={4}>{asset?.LastComms ? timeDifference(asset.LastComms) : "N/A"}</Col>
                    </Row>

                    <br />
                    <Row className="bold">
                        <Col xs={4}>Lat (°)</Col>
                        <Col xs={4}>Lon (°)</Col>
                        <Col xs={4}>Alt</Col>
                    </Row>
                    <Row>
                        <Col xs={4}>{formatCoord(asset?.Latitude)}</Col>
                        <Col xs={4}>{formatCoord(asset?.Longitude)}</Col>
                        <Col xs={4}>{formatAltitude(asset?.Altitude)}</Col>
                    </Row>
                </div>
            </Card.Body>

            {!isDossierPage && (
                <Card.Footer>
                    <Row className="text-center">
                        <Col>
                            <OverlayTrigger
                                placement="top"
                                overlay={renderTooltip("View Dossier (right-click to open in new tab)")}
                            >
                                <Button
                                    variant="dark"
                                    style={{ color: "rgb(235 215 147)" }}
                                    onClick={handleClick}
                                    onContextMenu={openInNewTab}
                                >
                                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <FaFolder style={{ marginBottom: "2px" }} />
                                        <span style={{ color: "white", lineHeight: "1.5" }}>&nbsp;Dossier</span>
                                    </div>
                                </Button>
                            </OverlayTrigger>
                        </Col>
                    </Row>
                </Card.Footer>
            )}
        </Card>
    );
};

SewCard.propTypes = {
    asset: PropTypes.shape({
        Id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        Name: PropTypes.string,
        Classification: PropTypes.string,
        Type: PropTypes.string,
        Status: PropTypes.string,
        LastComms: PropTypes.string,
        Latitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        Longitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        Altitude: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        CountryId: PropTypes.string
    }).isRequired,
    imageUrl: PropTypes.string
};

export default memo(SewCard);
